import React, { useCallback, useMemo } from 'react'
import classnames from 'classnames/bind'
import { AutomationTarget } from '../../../types'
import { getCssColorFromColorName } from '../../../utils/colors'
import { ColorNames, BlendedColorNames } from '../../../constants/colorVars'
import Icon, { IconStyle } from '../icon'
import HorizontalExpander from '../horizontalExpander'
import type { IconProps } from '../icon'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const TRANSITION_MS = 200

/**
 * InfoPill is a small, pill-shaped container that can display a small amount of information e.g status.
 *
 * The Icon can be specified by name (iconName) for simple solid icons colored based on the foreground color.
 * Optionally you can pass iconColor as well (which only applies to iconName approach)
 * ALTERNATIVELY the icon can be passed as a component (icon) for more complex icons.
 */
export interface InfoPillProps extends AutomationTarget {
  iconName?: string
  iconColor?: ColorNames
  iconStyle?: IconStyle
  icon?: React.FC<IconProps> | React.ReactNode
  iconOnRight?: boolean
  content?: React.ReactNode
  size?: 'x-small' | 'small' | 'medium' | 'large'
  backgroundColor?: ColorNames | BlendedColorNames
  foregroundColor?: ColorNames
  includeBorder?: boolean
  className?: string
  contentClassName?: string
  style?: React.CSSProperties
  extraPadding?: 'small' | 'medium'
  fontWeight?: 'normal' | 'bold'
  label?: React.ReactNode
  interactive?: 'none' | 'button' | 'icon'
  disabled?: boolean
  onClick?: () => void
  children?: React.ReactNode
  /**
   * Native tooltip text shown on hover. Useful to reveal the full content when
   * the pill is truncated (cap width / ellipsis via `className` or `style`).
   */
  title?: string
}

const InfoPill: React.FC<InfoPillProps> = ({
  iconName,
  iconColor,
  icon,
  iconOnRight = false,
  content,
  elmId = 'info-pill',
  size = 'small',
  backgroundColor = ColorNames.OffWhiteDark,
  foregroundColor = ColorNames.Gray,
  includeBorder = false,
  className,
  style,
  extraPadding,
  fontWeight = 'bold',
  label,
  iconStyle = 'solid',
  interactive = 'none',
  disabled = false,
  contentClassName,
  onClick,
  title,
}) => {
  // extract raw color values from color names
  const backgroundColorRaw = getCssColorFromColorName(backgroundColor)
  const foregroundColorRaw = getCssColorFromColorName(foregroundColor)

  const colorStyles = useMemo(
    () => ({
      background: backgroundColorRaw,
      color: foregroundColorRaw,
      border: `1px solid ${
        includeBorder ? foregroundColorRaw : backgroundColorRaw
      }`,
    }),
    [backgroundColorRaw, foregroundColorRaw, includeBorder]
  )

  // get icon component if iconName is provided
  const _icon: React.ReactNode = useMemo(
    () =>
      // eslint-disable-next-line no-nested-ternary
      iconName ? (
        <Icon
          name={iconName}
          color={iconColor || 'inherit'}
          iconStyle={iconStyle}
          fitted
        />
      ) : React.isValidElement(icon) ? (
        React.cloneElement(icon as React.ReactElement, {
          // eslint-disable-next-line
          className: cx(backgroundColor, icon?.props?.className),
        })
      ) : (
        (icon as React.ReactNode)
      ),
    [iconName, iconColor, iconStyle, icon, backgroundColor]
  )

  const renderPill = useCallback(
    () => (
      <div
        className={cx(
          'info-pill',
          size,
          backgroundColor,
          `interactive-${interactive}`,
          { disabled },
          { 'icon-on-right': iconOnRight },
          { 'include-border': includeBorder },
          { 'extra-padding-small': extraPadding === 'small' },
          { 'extra-padding-medium': extraPadding === 'medium' },
          { 'font-weight-normal': fontWeight === 'normal' },
          className
        )}
        style={{ ...colorStyles, ...style }}
        data-elm-id={elmId || 'info_pill'}
        title={title}
        onClick={onClick}
      >
        {interactive === 'none' ? (
          <div className={cx('content', contentClassName)}>
            {!iconOnRight && _icon}
            {content && <div className={cx('text')}>{content}</div>}
            {iconOnRight && _icon}
          </div>
        ) : (
          <HorizontalExpander transitionDurationMs={TRANSITION_MS}>
            <div className={cx('content', contentClassName)}>
              {!iconOnRight && _icon}
              {content && <div className={cx('text')}>{content}</div>}
              {iconOnRight && _icon}
            </div>
          </HorizontalExpander>
        )}
      </div>
    ),
    [
      size,
      backgroundColor,
      interactive,
      disabled,
      iconOnRight,
      includeBorder,
      extraPadding,
      fontWeight,
      className,
      colorStyles,
      style,
      elmId,
      contentClassName,
      _icon,
      content,
      title,
    ]
  )

  return (
    <>
      {label ? (
        <div data-elm-id={`${elmId}-container`} className={cx('container')}>
          {renderPill()}
          <div className={cx('label')}>{label}</div>
        </div>
      ) : (
        renderPill()
      )}
    </>
  )
}

interface GroupProps {
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
}

const Group: React.FC<GroupProps> = ({ className, children, style }) => (
  <div className={cx('info-pill-group', className)} style={style}>
    {children}
  </div>
)

interface InfoPillStatic {
  Group: typeof Group
}

const InfoPillWithStatic: React.FC<InfoPillProps> & InfoPillStatic =
  InfoPill as React.FC<InfoPillProps> & InfoPillStatic

InfoPillWithStatic.Group = Group

export default InfoPillWithStatic
